import React, { useRef, useState } from 'react';
import { Control, Controller } from 'react-hook-form';

import { FormError } from '../FormElements/FormElements';

import styles from './JsonFieldInput.module.scss';
import { jsonStringToRows, Row, rowsToJsonString, tryParse } from './utils';

type Mode = 'rows' | 'json';

interface JsonFieldInputFieldProps {
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  keyPlaceholder: string;
  valuePlaceholder: string;
  addLabel: string;
}

const JsonTextArea: React.FC<{
  value: string;
  hasError: boolean;
  onChange: (v: string) => void;
}> = ({ value, hasError, onChange }) => {
  const gutterRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const lineCount = value ? value.split('\n').length : 1;

  const syncScroll = () => {
    if (gutterRef.current && textareaRef.current) {
      gutterRef.current.scrollTop = textareaRef.current.scrollTop;
    }
  };

  return (
    <div className={`${styles.jsonEditor} ${hasError ? styles.jsonEditorError : ''}`}>
      <div
        ref={gutterRef}
        className={styles.lineNumbers}
        aria-hidden="true"
      >
        {Array.from({ length: lineCount }, (_, i) => (
          <span key={i + 1}>{i + 1}</span>
        ))}
      </div>
      <textarea
        ref={textareaRef}
        className={styles.jsonTextarea}
        value={value}
        spellCheck={false}
        placeholder={'{\n  "key": "value"\n}'}
        onChange={e => onChange(e.target.value)}
        onScroll={syncScroll}
      />
    </div>
  );
};

const JsonFieldInputField: React.FC<JsonFieldInputFieldProps> = ({
  value,
  onChange,
  onBlur,
  keyPlaceholder,
  valuePlaceholder,
  addLabel,
}) => {
  const [mode, setMode] = useState<Mode>('rows');
  const [rows, setRows] = useState<Row[]>(() => jsonStringToRows(value));
  const [jsonText, setJsonText] = useState<string>(() => {
    const res = tryParse(value);
    return res.valid && Object.keys(res.obj).length ? JSON.stringify(res.obj, null, 2) : '';
  });
  const [jsonError, setJsonError] = useState<string | null>(null);

  const switchToJson = () => {
    const serialized = rowsToJsonString(rows);
    const pretty = serialized ? JSON.stringify(JSON.parse(serialized), null, 2) : '';
    setJsonText(pretty);
    setJsonError(null);
    setMode('json');
    onChange(serialized);
  };

  const switchToRows = () => {
    const res = tryParse(jsonText);
    if ('error' in res) {
      setJsonError(res.error);
      return;
    }
    const nextRows = jsonStringToRows(jsonText);
    setRows(nextRows);
    setMode('rows');
    onChange(rowsToJsonString(nextRows));
  };

  const updateRow = (id: string, field: 'key' | 'value', val: string) => {
    const nextRows = rows.map(r => (r.id === id ? { ...r, [field]: val } : r));
    setRows(nextRows);
    onChange(rowsToJsonString(nextRows));
  };

  const removeRow = (id: string) => {
    const nextRows = rows.filter(r => r.id !== id);
    setRows(nextRows);
    onChange(rowsToJsonString(nextRows));
  };

  const addRow = () => {
    const nextRows = [...rows, { id: crypto.randomUUID(), key: '', value: '' }];
    setRows(nextRows);
    onChange(rowsToJsonString(nextRows));
  };

  const onJsonChange = (text: string) => {
    setJsonText(text);
    const res = tryParse(text);
    if ('error' in res) {
      setJsonError(res.error);
    } else {
      setJsonError(null);
    }
    onChange(text);
  };

  return (
    <div
      className={styles.container}
      onBlur={onBlur}
    >
      <div className={styles.tabRow}>
        <div className={styles.tabPill}>
          <button
            type="button"
            className={`${styles.tabBtn} ${mode === 'rows' ? styles.tabBtnActive : ''}`}
            onClick={switchToRows}
          >
            Rows
          </button>
          <button
            type="button"
            className={`${styles.tabBtn} ${mode === 'json' ? styles.tabBtnActive : ''}`}
            onClick={switchToJson}
          >
            JSON
          </button>
        </div>
      </div>

      {mode === 'rows' && (
        <div className={styles.rowsList}>
          {rows.map(row => (
            <div
              key={row.id}
              className={styles.row}
            >
              <input
                type="text"
                className={styles.rowInput}
                value={row.key}
                placeholder={keyPlaceholder}
                onChange={e => updateRow(row.id, 'key', e.target.value)}
              />
              <input
                type="text"
                className={styles.rowInput}
                value={row.value}
                placeholder={valuePlaceholder}
                onChange={e => updateRow(row.id, 'value', e.target.value)}
              />
              <button
                type="button"
                className={styles.removeBtn}
                aria-label="Remove row"
                onClick={() => removeRow(row.id)}
              >
                ✕
              </button>
            </div>
          ))}
          <button
            type="button"
            className={styles.addBtn}
            onClick={addRow}
          >
            + {addLabel}
          </button>
        </div>
      )}

      {mode === 'json' && (
        <JsonTextArea
          value={jsonText}
          hasError={!!jsonError}
          onChange={onJsonChange}
        />
      )}
      {mode === 'json' && jsonError && (
        <div className={styles.jsonError}>
          <span>⚠</span>
          <span>{jsonError}</span>
        </div>
      )}

      {mode === 'json' && <p className={styles.footnote}>Invalid JSON can&apos;t be saved or converted to rows.</p>}
    </div>
  );
};

interface JsonFieldInputProps {
  name: string;
  control: Control<any>;
  error?: string;
  keyPlaceholder?: string;
  valuePlaceholder?: string;
  addLabel?: string;
}

export const JsonFieldInput: React.FC<JsonFieldInputProps> = ({
  name,
  control,
  error,
  keyPlaceholder = 'Key',
  valuePlaceholder = 'Value',
  addLabel = 'Add row',
}) => (
  <>
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <JsonFieldInputField
          value={field.value || ''}
          onChange={field.onChange}
          onBlur={field.onBlur}
          keyPlaceholder={keyPlaceholder}
          valuePlaceholder={valuePlaceholder}
          addLabel={addLabel}
        />
      )}
    />
    {error && <FormError error={error} />}
  </>
);
